import Breadcrumb from '@/components/Breadcrumb';
import { useEffect, useRef, useState } from 'react';
import TableComponent from '@/components/Table/TabelComponent';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { useGetDropDownTransactionTypesQuery } from '@/services/transactiontype.service';
import ButtonSpin, { ButtonSpinHandle } from '@/components/Button/ButtonSpin';
import moment from 'moment-timezone';
import { useGetDropDownUnitCodesQuery } from '@/services/unitcode.service';
import TableHeader from './_header';
import { useGetDropDownBusinessUnitsQuery } from '@/services/businessunit.service';
import { useQueryTransactionsMutation } from '@/services/transaction.service';
import { IDropdown } from '@/constants/default';
import Select from 'react-select';
import { TransactionResp } from '@/types/transaction.type';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import LimitPaginationComponent from '@/components/Table/LimitPaginationComponent';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { pdf } from '@react-pdf/renderer';
import PdfTransaction from './_pdf';
import useColorMode from '@/hook/useColorMode';
import { EnumStatus, ModeUI } from '@/constants/enum';

export const darkSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: '#1f2937', // Tailwind: bg-gray-800
    borderColor: state.isFocused ? '#3c50e0' : '#4b5563',
    color: '#fff',
    '&:hover': {
      borderColor: '#3c50e0',
    },
    '&:focus': {
      borderColor: '#3c50e0',
    },
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: '#1f2937',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused ? '#374151' : '#1f2937', // Tailwind: hover:bg-gray-700
    color: '#fff',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#fff',
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: '#3c50e0', // màu nền của tag
    borderRadius: '4px',
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: 'white', // màu chữ của tag
    fontWeight: '500',
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: 'white',
    ':hover': {
      backgroundColor: '#1e3a8a', // darker hover (blue-900)
      color: 'white',
    },
  }),
};

export const lightSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    backgroundColor: '#fff',
    borderColor: state.isFocused ? '#3c50e0' : '#d1d5db',
    color: '#000',
    '&:hover': {
      borderColor: '#3c50e0',
    },
    '&:focus': {
      borderColor: '#3c50e0',
    },
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: '#fff',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isFocused ? '#e5e7eb' : '#fff',
    color: '#000',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#000',
  }),
};

interface FilterForm {
  unit_codes: IDropdown[];
  unit_code_ids: string[];
  business_units: IDropdown[];
  business_unit_ids: string[];
  transaction_type_ids: string[];
  start_date: string;
  end_date: string;
}

const endDate = moment().endOf('day').toISOString();
const startDate = moment().startOf('day').subtract(30, 'days').toISOString();

export default function Transactions() {
  const [getTransactions, { data: transactions, isLoading }] =
    useQueryTransactionsMutation();
  const { data: transactionTypes } = useGetDropDownTransactionTypesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: businessUnits } = useGetDropDownBusinessUnitsQuery(
    {status: `${EnumStatus.ACTIVE},${EnumStatus.INAVTIVE}`},
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const { data: unitCodes } = useGetDropDownUnitCodesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { t } = useTranslation();
  const { register, handleSubmit, control, setValue } = useForm<FilterForm>({
    defaultValues: {
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).format('YYYY-MM-DD'),
    },
  });
  const btnSearchRef = useRef<ButtonSpinHandle>(null);
  const btnClearhRef = useRef<ButtonSpinHandle>(null);
  const [limit, setLimit] = useState(200);
  const [page, setPage] = useState(0);
  const [dataPagination, setDataPagination] = useState<TransactionResp[]>([]);
  const totalDeposit = transactions?.reduce((n, i) => n + i.amount_deposit, 0) || 0;
  const totalWithdraw = transactions?.reduce((n, i) => n + i.amount_withdraw, 0) || 0;
  const [isDownloading, setIsDownloading] = useState(false)
  const [mode, setMode] = useState<ModeUI>(useColorMode().mode)
  const selectStyles = mode === ModeUI.DARK ? darkSelectStyles : lightSelectStyles;

  useEffect(() => {
    const handler = () => {
      let updated = localStorage.getItem('color-theme');
      if(updated) {
        updated = JSON.parse(updated)
        if(updated === ModeUI.AUTO) {
          updated = window.matchMedia && window.matchMedia("(prefers-color-scheme:dark)").matches ? ModeUI.DARK : ModeUI.LIGHT
        }
        setMode(updated as ModeUI)
      }
    }

    window.addEventListener('local-storage-updated', handler);
    return () => window.removeEventListener('local-storage-updated', handler);
  }, [])

  const onSubmitFilter = handleSubmit((data: FilterForm) => {
    getTransactions({
      transaction_type_ids: data.transaction_type_ids,
      business_unit_ids: data.business_units.map((item) => item.value) || '',
      unit_code_ids: data.unit_codes.map((item) => item.value) || '',
      start_date:
        moment(data.start_date, 'YYYY-MM-DD').startOf('day').toISOString() || startDate,
      end_date: moment(data.end_date, 'YYYY-MM-DD').endOf('day').toISOString() || endDate,
    });
  });

  const clearSearch = () => {
    setValue('start_date', moment(startDate).format('YYYY-MM-DD'));
    setValue('end_date', moment(endDate).format('YYYY-MM-DD'));
    setValue('business_units', []);
    setValue('transaction_type_ids', []);
    setValue('unit_codes', []);
    btnClearhRef.current && btnClearhRef.current.toggleSpin(false);
  };

  useEffect(() => {
    btnSearchRef.current && btnSearchRef.current.toggleSpin(isLoading);
  }, [isLoading]);

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: string[],
  ): string[] => {
    const { checked, value } = e.target;
    return checked ? [...data, value] : data.filter((v) => v !== value);
  };

  const exportToExcel = (type = 'excel') => {
    if (transactions) {
      setIsDownloading(true);
      const headers = [
        t('transaction.business_unit'),
        t('transaction.category'),
        t('transaction.transaction_code'),
        t('transaction.date'),
        t('form.description'),
        t('transaction.deposit'),
        t('transaction.withdraw'),
        t('transaction.link_images'),
      ];

      const data = transactions.map((item) => [
        item.business_unit_name,
        item.transaction_type_name,
        `${item.unit_code_code} - ${item.unit_code_name}`,
        moment(item.date).format('MMM DD YYYY'),
        item.description,
        item.amount_deposit > 0 ? item.amount_deposit : 0,
        item.amount_withdraw > 0 ? item.amount_withdraw : 0,
        item.images?.map((image) => image.image_url).join(', ') || '',
      ]);

      const worksheetData = [headers, ...data];
      // Convert JSON data to worksheet
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);
      if (type === 'excel') {
        // Create a new workbook and append worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        // Write workbook and create a Blob
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
        setIsDownloading(false);
        // Save the file
        saveAs(fileData, 'transaction.xlsx');
      } else {
        const data = XLSX.utils.sheet_to_csv(ws, { FS: ';' });
        const fileData = new Blob([data], { type: 'text/csv' });
        setIsDownloading(false);
        // Save the file
        saveAs(fileData, 'transaction.csv');
      }
    }
  };

  const exportToPdf = async () => {
    if (transactions) {
      setIsDownloading(true);
      const doc = (
        <PdfTransaction
          data={transactions}
          totalDeposit={totalDeposit}
          totalWithdraw={totalWithdraw}
        />
      );
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      setIsDownloading(false);
      saveAs(blob, 'transaction.pdf');
    }
  };

  useEffect(() => {
    if (transactions) {
      setDataPagination(transactions.slice(page * limit, (page + 1) * limit));
    }
  }, [transactions, page, limit]);

  return (
    <>
      <Breadcrumb pageName={t('transaction.query')} />
      {/* {
        transactions && <div style={{height: "100vh", width: "100vw"}}>
          <PDFViewer width={1000} height={1000}>
            <PdfTransaction data={transactions} totalDeposit={totalDeposit} totalWithdraw={totalWithdraw} />
          </PDFViewer>
        </div>
      } */}

      <div className="flex flex-col gap-10">
        <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-5 xl:pb-4">
          <div className="flex flex-row gap-4">
            <div className="w-[300px] py-4 shadow-9 sm:py-6">
              <form onSubmit={onSubmitFilter}>
                <div className="flex justify-center mt-5 mb-5 gap-3">
                  <div>
                    <ButtonSpin
                      ref={btnClearhRef}
                      title={t('transaction.clear')}
                      classBtn="flex justify-center items-center block w-full rounded bg-gray-400 p-1.5 px-3 text-center font-medium text-black transition hover:bg-opacity-90 disabled:opacity-50"
                      type="button"
                      icon={<i className="fa-light fa-trash me-3 text-black" />}
                      isDisabled={isLoading}
                      onClick={clearSearch}
                    />
                  </div>
                  <div>
                    <ButtonSpin
                      ref={btnSearchRef}
                      title={t('transaction.search')}
                      type="submit"
                      icon={<i className="fa-light fa-magnifying-glass me-3" />}
                      isDisabled={isLoading}
                    />
                  </div>
                  <div>
                    {/* <ButtonSpin
                      ref={btnDownloadhRef}
                      title={t("transaction.export")}
                      type="button"
                      onClick={exportToExcel}
                      classBtn="flex w-full justify-center items-center rounded bg-primary p-1.5 px-3 text-white hover:bg-opacity-90 disabled:bg-opacity-70"
                      icon={<i className="fa-light fa-file-excel me-3" />}
                      isDisabled={transactions?.length === 0 || !transactions}
                    /> */}
                    <Menu>
                      <MenuButton
                        disabled={!transactions || isDownloading}
                        className="flex w-full justify-center items-center rounded bg-primary p-1.5 px-3 text-white hover:bg-opacity-90 disabled:bg-opacity-70"
                      >
                        <i className="fa-light fa-download me-3" />
                        {t('transaction.export')}
                      </MenuButton>

                      <MenuItems
                        transition
                        anchor="bottom end"
                        className="w-52 origin-top-right rounded-xl border border-gray-300 dark:border-white/5 bg-white p-1 text-sm/6 text-black dark:text-white dark:bg-boxdark transition duration-100 ease-out [--anchor-gap:--spacing(1)] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
                      >
                        <MenuItem>
                          <button
                            type="button"
                            className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-white/10 hover:font-bold"
                            onClick={() => exportToExcel('excel')}
                          >
                            <i className="fa-light fa-file-excel fa-lg me-1" />
                            Excel
                          </button>
                        </MenuItem>
                        <MenuItem>
                          <button
                            type="button"
                            className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-white/10 hover:font-bold"
                            onClick={() => exportToExcel('csv')}
                          >
                            <i className="fa-light fa-file-csv fa-lg me-1" />
                            Csv
                          </button>
                        </MenuItem>
                        <MenuItem>
                          <button
                            type="button"
                            className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-white/10 hover:font-bold"
                            onClick={exportToPdf}
                          >
                            <i className="fa-light fa-file-pdf fa-lg me-1" />
                            Pdf
                          </button>
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3 flex items-center">
                  <label className="block dark:text-white w-25">
                    {t('transaction.start_date')}:
                  </label>
                  <input
                    type="date"
                    max={moment().format('YYYY-MM-DD')}
                    className="custom-input-date-1 w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:opacity-75"
                    {...register('start_date')}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3 flex items-center">
                  <label className="block dark:text-white w-25">
                    {t('transaction.end_date')}:
                  </label>
                  <input
                    type="date"
                    max={moment().format('YYYY-MM-DD')}
                    className="custom-input-date-1 w-full rounded border-[1.5px] border-stroke bg-transparent py-1.5 px-2 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary disabled:opacity-75"
                    {...register('end_date')}
                  />
                </div>
                <div className="mb-3">
                  <label className="mb-1 block font-medium dark:text-white">
                    {t('transaction.business_unit')}
                  </label>
                  <Controller
                    name="business_units"
                    defaultValue={[]}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        key={mode.toString()}
                        styles={selectStyles}
                        options={businessUnits}
                        isMulti={true}
                        className="react-select-container dark:text-white"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                  <div className="mt-4"></div>
                </div>
                <div className="mb-3">
                  <label className="mb-1 block font-medium dark:text-white">
                    {t('transaction.category')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {transactionTypes?.map((item) => (
                      <div key={item.value}>
                        <label
                          htmlFor={`transactiontype-${item.value}-check`}
                          className="flex cursor-pointer select-none items-center"
                        >
                          <Controller
                            name={`transaction_type_ids`}
                            control={control}
                            render={({ field: { value, onChange, ref } }) => {
                              const checked = value?.indexOf(item.value) >= 0 || false;
                              return (
                                <div className="relative" ref={ref}>
                                  <input
                                    type="checkbox"
                                    id={`transactiontype-${item.value}-check`}
                                    className="sr-only"
                                    value={item.value}
                                    onChange={(e) =>
                                      onChange(handleCheckboxChange(e, value || []))
                                    }
                                  />
                                  <div
                                    className={classNames(
                                      'mr-4 flex h-5 w-5 items-center justify-center rounded border',
                                      {
                                        'border-primary bg-gray-50 dark:bg-transparent':
                                          checked,
                                      },
                                    )}
                                  >
                                    <span
                                      className={classNames('h-2.5 w-2.5 rounded-sm', {
                                        'bg-primary': checked,
                                      })}
                                    ></span>
                                  </div>
                                </div>
                              );
                            }}
                          />
                          <span className="capitalize">{item.label}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <label className="mb-1 block font-medium dark:text-white">
                    {t('transaction.transaction_code')}
                  </label>
                  <Controller
                    name="unit_codes"
                    defaultValue={[]}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        key={mode.toString()}
                        styles={selectStyles}
                        options={unitCodes}
                        isMulti={true}
                        className="react-select-container"
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </div>
              </form>
            </div>

            <div className="flex-1 overflow-x-auto mt-3 grow">
              <LimitPaginationComponent
                list={[200, 500, 1000, 0]}
                limit={limit}
                onChange={(s: number) => {
                  setPage(0);
                  setLimit(s === 0 ? transactions?.length || 0 : s);
                }}
              />
              <TableComponent
                data={
                  dataPagination.length > 0
                    ? [
                        ...dataPagination,
                        {
                          description: 'Total',
                          amount_deposit: totalDeposit,
                          amount_withdraw: totalWithdraw,
                        } as TransactionResp,
                      ]
                    : dataPagination
                }
                columns={TableHeader()}
                pageSize={limit}
                isPagination={true}
                currentPage={page}
                totalPages={Math.ceil((transactions?.length || 0) / limit)}
                onChangePage={(page: number) => setPage(page)}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
